import { z } from 'zod';

import { WeekdaySchema } from '../enums';
import { HHmmSchema, IdSchema, KopecksSchema } from './common';
import { ActivityRefSchema } from './gathering';
import { MemberPublicSchema } from './member';

/**
 * Место запасного (ADR-10, Stage 6 задача 2a): статус места, приглашение своего по ссылке,
 * приём приглашения по deep link `reserve_<token>`.
 */

/** Токен приглашения — часть start-параметра Telegram (`[A-Za-z0-9_-]`). */
export const ReserveTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{8,60}$/, 'Некорректный токен');
export const ReserveTokenInputSchema = z.object({ token: ReserveTokenSchema });

export const ReserveStateSchema = z.enum(['NONE', 'FREE', 'INVITED', 'TAKEN']);
export type ReserveState = z.infer<typeof ReserveStateSchema>;

export const ReserveInvitePublicSchema = z.object({
  id: IdSchema,
  /** `https://t.me/<bot>?startapp=reserve_<token>`; null — бот не настроен */
  link: z.string().url().nullable(),
  token: ReserveTokenSchema,
  expiresAt: z.string().datetime(),
  createdByMe: z.boolean(),
});
export type ReserveInvitePublic = z.infer<typeof ReserveInvitePublicSchema>;

export const ReserveStatusSchema = z.object({
  squadId: IdSchema,
  /** NONE — у отряда нет места запасного (`reserveCapacity = 0`) */
  state: ReserveStateSchema,
  reserveCapacity: z.number().int().nonnegative(),
  holder: MemberPublicSchema.nullable(),
  invite: ReserveInvitePublicSchema.nullable(),
  rotationHint: z.string().nullable(),
  reservePriceRatio: z.number(),
  /** Цена сезона отряда и доля запасного; null — сезона пока нет */
  seasonPriceKopecks: KopecksSchema.nullable(),
  reservePriceKopecks: KopecksSchema.nullable(),
  canInvite: z.boolean(),
  /** «Найти запасного в городе» доступно (фиче-флаг `feature.openSeats`, Stage 11) */
  poolSearchAvailable: z.boolean(),
  /** Место запасного уже отдано в набор города (`OpenSeat kind RESERVE`) */
  inPool: z.boolean(),
});
export type ReserveStatus = z.infer<typeof ReserveStatusSchema>;

export const ReserveInviteProblemSchema = z.enum([
  'not_found',
  'expired',
  'used',
  'taken',
  'squad_closed',
  'already_member',
  'already_in_squad',
]);
export type ReserveInviteProblem = z.infer<typeof ReserveInviteProblemSchema>;

/** Что видит приглашённый до принятия: отряд, кто позвал, срок, цена; и что мешает принять. */
export const ReserveInvitePreviewSchema = z.object({
  token: ReserveTokenSchema,
  valid: z.boolean(),
  problem: ReserveInviteProblemSchema.nullable(),
  squad: z
    .object({
      id: IdSchema,
      name: z.string().nullable(),
      activity: ActivityRefSchema,
      cityId: IdSchema,
      cityName: z.string(),
      weekday: WeekdaySchema,
      startTime: HHmmSchema,
      capacity: z.number().int().positive(),
      districtNames: z.array(z.string()),
    })
    .nullable(),
  invitedBy: z.string().nullable(),
  expiresAt: z.string().datetime().nullable(),
  rotationHint: z.string().nullable(),
  reservePriceKopecks: KopecksSchema.nullable(),
  /** Приглашённому нужно пройти онбординг (город и активность предзаполняются) */
  needsOnboarding: z.boolean(),
});
export type ReserveInvitePreview = z.infer<typeof ReserveInvitePreviewSchema>;

export const ReserveAcceptResultSchema = z.object({
  squadId: IdSchema,
  membershipId: IdSchema,
  /** Есть открытый сезон — можно идти на экран оплаты */
  seasonId: IdSchema.nullable(),
});
export type ReserveAcceptResult = z.infer<typeof ReserveAcceptResultSchema>;
